import { CmsPageType } from '@prisma-clinic/client'
import deburr from 'lodash/deburr'

import { Locale } from '../commonFormatters'

const NON_PATH = /^([a-z][a-z0-9+\-.]*:|\/\/|#|\?)/i
const HTTP_URL = /^https?:\/\//i

/**
 * Deburr, lowercase, collapse every run of non-alphanumerics into a single dash.
 *
 * Deliberately not a kebab-case converter: those split alphanumeric runs, turning a name like
 * `B12` into `b-12`. Slug stability matters more than that particular prettiness.
 */
export const toUrlSlug = (value: string): string =>
    deburr(value)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(?:^-+)|(?:-+$)/g, '')

/**
 * The canonical url form is *without* a trailing slash, matching every url the site already
 * serves. One implementation, shared by the seo builders, the link resolver and redirect targets —
 * picking a form and not applying it everywhere is worse than picking the other one.
 *
 * Four properties: idempotent; query and hash ride along untouched; the root `/` keeps its slash
 * because there it is the path; and a third-party absolute url must never be handed here, since
 * trimming someone else's trailing slash can change what it points at.
 */
export const toCanonicalPath = (url: string): string => {
    if (!url) return url
    if (NON_PATH.test(url) && !HTTP_URL.test(url)) return url

    const separatorIndex = url.search(/[?#]/)
    const path = separatorIndex === -1 ? url : url.slice(0, separatorIndex)
    const suffix = separatorIndex === -1 ? '' : url.slice(separatorIndex)

    const origin = HTTP_URL.test(path) ? (/^https?:\/\/[^/]*/i.exec(path)?.[0] ?? '') : ''
    const pathname = path.slice(origin.length)

    if (pathname === '' || pathname === '/') return url
    if (!pathname.endsWith('/')) return url

    return `${path.slice(0, -1)}${suffix}`
}

export const prefixedPageTypes = [CmsPageType.ARTICLE, CmsPageType.LANDING] as const

export type PrefixedCmsPageType = (typeof prefixedPageTypes)[number]

export const isPrefixedPageType = (type: CmsPageType): type is PrefixedCmsPageType =>
    (prefixedPageTypes as readonly CmsPageType[]).includes(type)

/**
 * Prefixed page types live flat under a fixed, code-defined path, and the path is per locale so
 * each language reads naturally. They cannot have a parent.
 */
export const pageTypePrefixes: Record<PrefixedCmsPageType, Record<Locale, string>> = {
    [CmsPageType.ARTICLE]: { hu: 'cikkek', en: 'articles' },
    [CmsPageType.LANDING]: { hu: 'ajanlatok', en: 'offers' },
}

/**
 * First path segments owned by fixed routes in the public web app, which are matched ahead of the
 * cms splat route. A page claiming one of these would be permanently shadowed with no way for the
 * editor to find out why, so the save is rejected instead.
 */
export const reservedSlugs: readonly string[] = [
    'about',
    'apply',
    'booking',
    'bookings',
    'care-examinations',
    'checkout',
    'confirm-email',
    'contact',
    'events',
    'for-experts',
    'forgot-password',
    'knowledge-base',
    'lifestyle-programs',
    'login',
    'packages',
    'privacy-policy',
    'register',
    'reset-password',
    'terms',
    'video-consultations',
    'webinar',
    'webinars',
]

export const composePageSlug = ({ prefix, leaf }: { prefix: string; leaf: string }): string =>
    [prefix, leaf].filter(Boolean).join('/')

/**
 * Strip origin, query and hash, then run every segment through the same slugifier a page slug is
 * built with, so pasting a full old-site url just works.
 *
 * Storage, lookup and the admin's preview of what will be stored all call this, which is the only
 * way a redirect matches whatever form the url was entered in.
 */
export const normalizeRedirectPath = (value: string): string => {
    let path = value.trim()

    if (HTTP_URL.test(path)) {
        try {
            path = new URL(path).pathname
        } catch {
            // Not a parseable url after all; carry on treating it as a raw path.
        }
    }

    path = path.split(/[?#]/)[0] ?? ''

    try {
        path = decodeURIComponent(path)
    } catch {
        // Keep the raw form rather than losing the value to a malformed escape.
    }

    return path.split('/').map(toUrlSlug).filter(Boolean).join('/')
}

/**
 * Matches the youtube url forms an editor is likely to paste: a watch link, a share link, an
 * embed link or a short. Mirrors the pattern the educational-content module already validates
 * against, so the two agree on what counts as a video url.
 */
export const YOUTUBE_URL_PATTERN =
    /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/

export const getYoutubeVideoId = (url: string): string | undefined =>
    YOUTUBE_URL_PATTERN.exec(url.trim())?.[1]

/**
 * The privacy-preserving embed host: it sets no tracking cookie until the visitor actually plays
 * the video, which is the difference between a page that needs consent for an embed and one that
 * does not.
 */
export const buildYoutubeEmbedUrl = (url: string): string | undefined => {
    const id = getYoutubeVideoId(url)
    return id ? `https://www.youtube-nocookie.com/embed/${id}` : undefined
}

/** Exact hex only. Every other form would have to be parsed before it could be trusted. */
export const HEX_COLOR_PATTERN = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i

export const isHexColor = (value: unknown): value is string =>
    typeof value === 'string' && HEX_COLOR_PATTERN.test(value)

/**
 * The colours offered as one-click presets in the editor.
 *
 * The field stays free-form, so this is a shortcut to the brand palette rather than a limit — the
 * same pattern the spacer block's height presets use.
 */
export const CMS_COLOR_PRESETS: ReadonlyArray<{ name: string; value: string }> = [
    { name: 'primary', value: '#409834' },
    { name: 'yellow', value: '#e8cc17' },
    { name: 'dark', value: '#1b2419' },
    { name: 'gray', value: '#4b554a' },
    { name: 'white', value: '#ffffff' },
    { name: 'black', value: '#000000' },
]
